import type { LectureRecordApi } from '../services/lecture-records';
import { LectureRecordsPanel } from './lecture-records/LectureRecordsPanel';
import { lectureSnapshotSchema, userErrorMessage } from '@wx-weiqi/contracts';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentProps,
  type FormEvent,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import type {
  BoardPoint,
  ClassroomSgfData,
  FoxKifuRecordData,
  BoardSize,
  FoxKifuClassroomResearchBody,
  FoxKifuRecordSummary,
  StoneColor,
  EbookQuestion,
  StudentProblemSnapshot,
} from '@wx-weiqi/contracts';
import type { EbookClient } from '../services/ebooks';
import type { StudentPracticeClient } from '../services/student-practice-client';
import { downloadBoardJpeg, foxBoardCaptureFilename } from '../services/board-capture';
import { StudentPracticeQuestions } from './StudentPracticeQuestions';
import { StudentPracticeMarkerControl } from './StudentPracticeMarkerControl';
import {
  classroomTextbookQuestionAnnotations,
  type ClassroomTextbook,
} from '../services/classroom-textbooks';

import { pointKey } from '../domain/board-geometry';
import {
  createClassroomBranches,
  editClassroomBranch,
  selectClassroomBranch,
  startClassroomVariation,
  previousClassroomBranch,
  seekClassroomBranch,
  followClassroomSourceMove,
  type ClassroomBranches,
} from '../domain/classroom-branches';
import { classroomTextbookOpening } from '../domain/classroom-textbook-presentation';
import {
  formatNoteRange,
  noteFormatRange,
  noteRangeIsBold,
  type ClassroomNoteFormat,
} from '../domain/classroom-note-format';
import {
  CLASSROOM_NOTE_FONT_SIZES,
  readClassroomNote,
  type NoteSection,
} from '../domain/classroom-note-share';
import { ClassroomNoteShareDialog } from './ClassroomNoteShareDialog';
import {
  applyClassroomPlacement,
  applyClassroomSecondaryPlacement,
  appendClassroomRuleMoves,
  classroomRulePosition,
  classroomRecordHistory,
  classroomAlternateColorAtOffset,
  classroomAlternateColorAtFrame,
  nextClassroomAlternateColor,
  type ClassroomPlacementMode,
  type ClassroomFrame,
} from '../domain/classroom-lecture';
import { boardViewportForCorner, type HomeworkBoardCorner } from '../domain/homework-board-focus';
import { branchResearchVariation, researchVariationPrefix } from '../domain/fox-kifu-presentation';
import { aiVariationStoneLabels, presentAiCandidates } from '../domain/ai-candidate-presentation';
import {
  ZHIZI_COMPUTE_MODE_LABELS,
  zhiziWeightLabel,
  ZHIZI_EXCLUSIVE_TIER_LABELS,
} from '../domain/zhizi-profile';
import type {
  ClassroomSgfImportFailure,
  FoxKifuSearchInput,
  FoxKifuStore,
} from '../stores/fox-kifu-store';
import { AiCandidatePanel } from './AiCandidatePanel';
import { AiAccountSelector } from './AiAccountSelector';
import { selectedAccount } from '../stores/ai-account-selection';
import { BoardMoveProgress } from './BoardMoveProgress';
import { BoardSurface } from './BoardSurface';
import { ClassroomBoardSizeMenu } from './ClassroomBoardSizeMenu';
import { ClassroomControlButton } from './ClassroomControlButton';
import { ClassroomWorkspace } from './ClassroomWorkspace';
import { ClassroomPositionStatus } from './ClassroomPositionStatus';
import { FoxKifuRecordDetailsDialog } from './FoxKifuPage';
import {
  ReviewAnnotationLayer,
  type ReviewAnnotation,
  type ReviewAnnotationColor,
  type ReviewAnnotationTool,
  type ReviewBrushStroke,
  toggleReviewPointAnnotation,
} from './ReviewAnnotationLayer';
import { ReviewAnnotationPanel } from './ReviewAnnotationPanel';
import { ClassroomAnnotationToolbar } from './ClassroomAnnotationToolbar';
import { ClassroomSgfPanel } from './ClassroomSgfPanel';
import { ClassroomTextbookPicker } from './ClassroomTextbookPicker';
import { ClassroomTextbookPanel } from './ClassroomTextbookPanel';
import {
  classroomMarkerLabel,
  classroomResearchStoneLabels,
  classroomVisibleMarkers,
  type ClassroomMarkerMode,
} from '../domain/classroom-marker-display';
import { useBoardPanelDrag } from './useBoardPanelDrag';
import { useBoardWheelNavigation } from './useBoardWheelNavigation';
import { VerticalMoveSlider } from './VerticalMoveSlider';
import {
  ArrowLeftIcon,
  Pencil2Icon,
  ChevronDownIcon,
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  Share1Icon,
} from '@radix-ui/react-icons';
import { ClassroomStudioControls } from './ClassroomStudioControls';
import './classroom-lecture.css';
import './classroom-studio.css';
import './student-practice.css';

type MarkerMode = ClassroomMarkerMode;

const MARKER_MODES: readonly MarkerMode[] = ['LAST_ONE', 'LAST_FIVE', 'ALL'];
const BOARD_CORNER_LABELS: Readonly<Record<HomeworkBoardCorner, string>> = {
  TOP_LEFT: '左上角',
  TOP_RIGHT: '右上角',
  BOTTOM_LEFT: '左下角',
  BOTTOM_RIGHT: '右下角',
};
const NOTE_TEXT_COLORS = Object.freeze([
  { label: '黑色', value: '#000000' },
  { label: '红色', value: '#b53a32' },
  { label: '橙色', value: '#b8611b' },
  { label: '绿色', value: '#2f7357' },
  { label: '蓝色', value: '#2f679b' },
]);
const NOTE_HIGHLIGHT_COLORS = Object.freeze([
  { label: '红色', value: '#f7b6ac' },
  { label: '黄色', value: '#ffe58a' },
  { label: '蓝色', value: '#bcdaf4' },
  { label: '无颜色', value: 'transparent' },
]);

function emptyFrame(size: BoardSize): ClassroomFrame {
  return Object.freeze({
    moveNumber: 0,
    stones: Object.freeze(Array<null>(size * size).fill(null)),
    lastMove: null,
    color: null,
    ruleHistory: null,
  });
}

function labeledClassroomMoves(
  frames: readonly ClassroomFrame[],
  cursor: number,
  markerMode: MarkerMode,
  boardSize: BoardSize,
  customMarkerCount: number,
) {
  const currentStones = frames[cursor]?.stones ?? [];
  const candidates = classroomVisibleMarkers(
    frames.slice(1, cursor + 1),
    markerMode,
    customMarkerCount,
  ).filter(
    (
      candidate,
    ): candidate is ClassroomFrame & {
      readonly lastMove: BoardPoint;
      readonly color: StoneColor;
    } =>
      candidate.lastMove !== null &&
      candidate.color !== null &&
      currentStones[candidate.lastMove.y * boardSize + candidate.lastMove.x] === candidate.color,
  );
  const visible = candidates;
  const latest = new Map<string, (typeof visible)[number]>();
  visible.forEach((candidate) => latest.set(pointKey(candidate.lastMove), candidate));
  return [...latest.values()].map((candidate) => ({
    point: candidate.lastMove,
    label: String(candidate.moveNumber),
    color: candidate.color,
  }));
}

function playerName(
  record: FoxKifuRecordSummary | null,
  color: StoneColor,
): { readonly name: string; readonly rank: string } {
  if (record === null) {
    return { name: color === 'BLACK' ? '黑棋' : '白棋', rank: '课堂摆棋' };
  }
  const player = color === 'BLACK' ? record.black : record.white;
  return { name: player.nickname, rank: player.rank };
}

export function ClassroomLecturePage({
  store,
  onBack,
  teacherName = '老师',
  ebookClient,
  studentPracticeClient,
  lectureRecords,
  lectureRecordScope,
}: {
  readonly lectureRecords?: LectureRecordApi;
  readonly lectureRecordScope?: string;
  readonly store: FoxKifuStore;
  readonly onBack: () => void;
  readonly teacherName?: string;
  readonly ebookClient?: Pick<EbookClient, 'list' | 'document'>;
  readonly studentPracticeClient?: StudentPracticeClient;
}) {
  const [recordMode, setRecordMode] = useState(false);
  const studentPractice = studentPracticeClient !== undefined;
  const studio = !studentPractice;
  const noteTitle = studentPractice ? '笔记心得' : '课堂笔记';
  const [practiceProblem, setPracticeProblem] = useState<StudentProblemSnapshot | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureMessage, setCaptureMessage] = useState('');
  const [panelsSwapped, setPanelsSwapped] = useState(false);
  const panelSwapButtonRef = useRef<HTMLButtonElement>(null);
  const restoreSwapFocusRef = useRef(false);
  useLayoutEffect(() => {
    if (!restoreSwapFocusRef.current) return;
    restoreSwapFocusRef.current = false;
    panelSwapButtonRef.current?.focus({ preventScroll: true });
    panelSwapButtonRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [panelsSwapped]);
  const state = useSyncExternalStore(store.subscribe, store.snapshot);
  const sgfInputRef = useRef<HTMLInputElement>(null);
  const [sgfRecords, setSgfRecords] = useState<readonly ClassroomSgfData[]>([]);
  const [sgfImportFailures, setSgfImportFailures] = useState<readonly ClassroomSgfImportFailure[]>(
    [],
  );
  const [sgfPanelState, setSgfPanelState] = useState<'HIDDEN' | 'OPEN' | 'MINIMIZED'>('HIDDEN');
  const sgfRestoreButtonRef = useRef<HTMLButtonElement>(null);
  const [localSgf, setLocalSgf] = useState<ClassroomSgfData | null>(null);
  const [textbook, setTextbook] = useState<ClassroomTextbook | null>(null);
  const [textbookPickerOpen, setTextbookPickerOpen] = useState(false);
  const [textbookPanelOpen, setTextbookPanelOpen] = useState(false);
  const [textbookQuestion, setTextbookQuestion] = useState<{
    bookId: string;
    question: EbookQuestion;
  } | null>(null);
  const textbookRestoreButtonRef = useRef<HTMLButtonElement>(null);
  const pageRef = useRef<HTMLElement | null>(null);
  const boardStageRef = useRef<HTMLDivElement | null>(null);
  const notesEditorRef = useRef<HTMLDivElement | null>(null);
  const notesSelectionRef = useRef<Range | null>(null);
  const boardSizeControlRef = useRef<HTMLDivElement | null>(null);
  const boardSizeButtonRef = useRef<HTMLButtonElement | null>(null);
  const foxSearchPanel = useBoardPanelDrag({ preserveWidth: studio });
  const [boardSize, setBoardSize] = useState<BoardSize>(19);
  const [boardSizeMenuOpen, setBoardSizeMenuOpen] = useState(false);
  const [pendingBoardSize, setPendingBoardSize] = useState<BoardSize | null>(null);
  const [lecturePosition, setLecturePosition] = useState<{
    frames: readonly ClassroomFrame[];
    cursor: number;
    branchWorkspace: ClassroomBranches | null;
  }>(() => ({ frames: [emptyFrame(19)], cursor: 0, branchWorkspace: null }));
  const { frames, cursor, branchWorkspace } = lecturePosition;
  const setFrames = (next: readonly ClassroomFrame[]) =>
    setLecturePosition((current) => ({ ...current, frames: next }));
  const activeBranch = branchWorkspace?.branches.find(
    (branch) => branch.id === branchWorkspace.activeId,
  );
  const [placementMode, setPlacementMode] = useState<ClassroomPlacementMode | null>(
    studentPractice ? 'ALTERNATE' : null,
  );
  const [alternateStartColor, setAlternateStartColor] = useState<StoneColor>('BLACK');
  const [alternateBaseCursor, setAlternateBaseCursor] = useState(0);
  const [alternateHint, setAlternateHint] = useState<'黑先' | '白先' | null>(null);
  const [notes, setNotes] = useState('');
  const [noteShare, setNoteShare] = useState<readonly NoteSection[] | null>(null);
  const [noteShareError, setNoteShareError] = useState<string | null>(null);
  const [noteTextColorMenuOpen, setNoteTextColorMenuOpen] = useState(false);
  const [noteHighlightMenuOpen, setNoteHighlightMenuOpen] = useState(false);
  const [noteTextColor, setNoteTextColor] = useState(NOTE_TEXT_COLORS[0]?.value ?? '#b53a32');
  const [noteHighlightColor, setNoteHighlightColor] = useState(
    NOTE_HIGHLIGHT_COLORS[1]?.value ?? '#ffe58a',
  );
  const [noteFontSize, setNoteFontSize] = useState(studio ? '22' : '16');
  const [noteBold, setNoteBold] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [activeSearch, setActiveSearch] = useState<FoxKifuSearchInput | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [foxSearchOpen, setFoxSearchOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<FoxKifuRecordSummary | null>(null);
  const [recordBacked, setRecordBacked] = useState(false);
  const [markerMode, setMarkerMode] = useState<MarkerMode>('LAST_ONE');
  const [customMarkerCount, setCustomMarkerCount] = useState(10);
  const [researchBase, setResearchBase] = useState<number | null>(null);
  const [classroomResearchPosition, setClassroomResearchPosition] =
    useState<FoxKifuClassroomResearchBody | null>(null);
  const [variationMoves, setVariationMoves] = useState<readonly BoardPoint[]>([]);
  const [researchCursor, setResearchCursor] = useState(0);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiConnectionOpen, setAiConnectionOpen] = useState(false);
  const [aiChecking, setAiChecking] = useState(false);
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [annotationTool, setAnnotationTool] = useState<ReviewAnnotationTool>('BRUSH');
  const [annotationColor, setAnnotationColor] = useState<ReviewAnnotationColor>('RED');
  const [annotationWidth, setAnnotationWidth] = useState(7);
  const [annotations, setAnnotations] = useState<readonly ReviewAnnotation[]>([]);
  const [boardZoomMenuOpen, setBoardZoomMenuOpen] = useState(false);
  const [boardCorner, setBoardCorner] = useState<HomeworkBoardCorner | null>(null);
  const frame = frames[cursor] ?? frames[0] ?? emptyFrame(boardSize);
  const totalMoves = Math.max(0, frames.length - 1);
  const sequenceColor = classroomAlternateColorAtOffset(
    alternateStartColor,
    cursor - alternateBaseCursor,
  );
  const alternateColor =
    placementMode === 'ALTERNATE'
      ? classroomAlternateColorAtFrame(
          alternateStartColor,
          cursor - alternateBaseCursor,
          frame.ruleHistory,
        )
      : (frame.ruleHistory?.nextColor ?? sequenceColor);
  const researching = researchBase !== null;
  const positionPending = state.pending === 'RESEARCH' || state.pending === 'SGF_IMPORT';
  const progressUnavailable = totalMoves === 0 || researching;
  const previousUnavailable = researching
    ? researchCursor === 0
    : activeBranch?.kind !== 'VARIATION' && cursor === 0;
  const nextUnavailable = researching
    ? researchCursor >= variationMoves.length
    : cursor >= totalMoves;
  const activeVariationMoves = useMemo(
    () => researchVariationPrefix(variationMoves, researchCursor),
    [researchCursor, variationMoves],
  );
  const baseStones = researching ? (state.research?.stones ?? frame.stones) : frame.stones;
  const researchNextColor = researching
    ? (state.research?.nextColor ?? classroomResearchPosition?.currentPlayer ?? alternateColor)
    : alternateColor;
  const moveLabels = useMemo(() => {
    if (researching && activeVariationMoves.length > 0) {
      return classroomResearchStoneLabels(
        activeVariationMoves,
        baseStones,
        boardSize,
        studio || studentPractice ? markerMode : 'ALL',
        customMarkerCount,
      );
    }
    return labeledClassroomMoves(frames, cursor, markerMode, boardSize, customMarkerCount);
  }, [
    activeVariationMoves,
    baseStones,
    boardSize,
    cursor,
    customMarkerCount,
    frames,
    markerMode,
    researching,
    studio,
    studentPractice,
  ]);
  const aiCandidates = useMemo(
    () => (aiEnabled ? presentAiCandidates(state.aiAnalysis) : []),
    [aiEnabled, state.aiAnalysis],
  );
  const aiVariationLabels = useMemo(
    () => aiVariationStoneLabels(state.aiVariation),
    [state.aiVariation],
  );
  const displayedStones = state.aiVariation?.stones ?? baseStones;
  const displayedLabels = state.aiVariation === null ? moveLabels : aiVariationLabels;
  const aiCandidateKeys = useMemo(
    () => new Set(aiCandidates.map((candidate) => pointKey(candidate.point))),
    [aiCandidates],
  );
  const boardViewport = useMemo(
    () => (boardCorner === null ? null : boardViewportForCorner(boardCorner)),
    [boardCorner],
  );
  const boardCoordinateEdges = useMemo(() => {
    if (boardCorner === null) return Object.freeze({ top: true, left: true });
    return Object.freeze({
      top: boardCorner === 'TOP_LEFT' || boardCorner === 'TOP_RIGHT',
      right: boardCorner === 'TOP_RIGHT' || boardCorner === 'BOTTOM_RIGHT',
      bottom: boardCorner === 'BOTTOM_LEFT' || boardCorner === 'BOTTOM_RIGHT',
      left: boardCorner === 'TOP_LEFT' || boardCorner === 'BOTTOM_LEFT',
    });
  }, [boardCorner]);
  const selectedRecordId = state.record?.record.recordId ?? selectedRecord?.recordId ?? null;
  const recordToolsAvailable =
    recordBacked &&
    selectedRecordId !== null &&
    (activeBranch === undefined || activeBranch.id === 'source-0');
  const researchAvailable =
    researching ||
    branchWorkspace !== null ||
    recordToolsAvailable ||
    localSgf !== null ||
    textbookQuestion !== null ||
    practiceProblem !== null ||
    placementMode === 'ALTERNATE';
  const detailPage = state.detailSearch?.page ?? 1;
  const detailPages = Math.max(1, Math.ceil((state.detailSearch?.total ?? 0) / 10));
  const searchInput = useMemo<FoxKifuSearchInput>(
    () => ({ query: query.trim(), from, to, page: 1, pageSize: 10 }),
    [from, query, to],
  );

  useLayoutEffect(() => {
    const stage = boardStageRef.current;
    if (stage === null) return;
    const fitBoard = () => {
      const bounds = stage.getBoundingClientRect();
      const nextSize = Math.floor(Math.min(bounds.width, bounds.height));
      stage.style.setProperty('--fox-board-size', `${Math.max(0, nextSize)}px`);
    };
    fitBoard();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fitBoard);
      return () => window.removeEventListener('resize', fitBoard);
    }
    const observer = new ResizeObserver(fitBoard);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [studio]);

  useEffect(() => {
    const update = () => setImmersive(document.fullscreenElement === pageRef.current);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);

  useEffect(() => {
    if (!studentPractice) void store.refreshAiStatus();
    return () => {
      store.cancelAi();
      store.clearResearch();
    };
  }, [store, studentPractice]);

  useEffect(() => {
    if (alternateHint === null) return;
    const timer = window.setTimeout(() => setAlternateHint(null), 1_000);
    return () => window.clearTimeout(timer);
  }, [alternateHint]);

  useEffect(() => {
    if (!boardSizeMenuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !boardSizeControlRef.current?.contains(event.target)) {
        setBoardSizeMenuOpen(false);
        setPendingBoardSize(null);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setBoardSizeMenuOpen(false);
      setPendingBoardSize(null);
      boardSizeButtonRef.current?.focus();
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [boardSizeMenuOpen]);

  useEffect(() => {
    if (!aiEnabled || state.aiAvailable !== true || state.pending !== null) return;
    const timer = window.setTimeout(() => {
      const analysis =
        recordToolsAvailable &&
        selectedRecordId !== null &&
        classroomResearchPosition === null &&
        placementMode !== 'ALTERNATE'
          ? store.analyze(
              selectedRecordId,
              researchBase ?? cursor,
              researchBase === null ? [] : activeVariationMoves,
            )
          : store.analyzeClassroom(
              classroomResearchPosition !== null
                ? appendClassroomRuleMoves(classroomResearchPosition, activeVariationMoves)
                : classroomRulePosition(frame.stones, boardSize, alternateColor, frame.ruleHistory),
            );
      void analysis.catch(() => undefined);
    }, 40);
    return () => window.clearTimeout(timer);
  }, [
    activeVariationMoves,
    aiEnabled,
    alternateColor,
    boardSize,
    baseStones,
    classroomResearchPosition,
    cursor,
    frame.stones,
    frame.ruleHistory,
    recordToolsAvailable,
    placementMode,
    researchBase,
    researchNextColor,
    selectedRecordId,
    state.aiAvailable,
    state.aiSelectedConfigurationId,
    state.pending,
    store,
  ]);

  useEffect(() => {
    const navigate = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        detailsOpen ||
        textbookPickerOpen ||
        noteShare !== null ||
        state.pending !== null
      )
        return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        navigateLecture('PREVIOUS');
      }
      if (event.key === 'ArrowRight') {
        navigateLecture('NEXT');
      }
      if (event.key === 'Home') {
        navigateLecture('FIRST');
      }
      if (event.key === 'End') {
        navigateLecture('LAST');
      }
    };
    window.addEventListener('keydown', navigate);
    return () => window.removeEventListener('keydown', navigate);
  }, [
    detailsOpen,
    textbookPickerOpen,
    noteShare,
    frames.length,
    researchCursor,
    researching,
    state.pending,
    variationMoves.length,
    branchWorkspace !== null,
    aiEnabled,
  ]);

  const commitClassroomEdit = (nextFrames: readonly ClassroomFrame[], nextCursor: number) => {
    setLecturePosition((current) =>
      current !== lecturePosition
        ? current
        : {
            frames: nextFrames,
            cursor: nextCursor,
            branchWorkspace:
              current.branchWorkspace === null
                ? null
                : editClassroomBranch(
                    current.branchWorkspace,
                    nextFrames,
                    current.cursor,
                    nextCursor,
                  ),
          },
    );
  };

  const switchClassroomBranch = (id: string) => {
    if (branchWorkspace === null || store.snapshot().pending !== null) return;
    const target = branchWorkspace.branches.find((branch) => branch.id === id);
    if (target === undefined) return;
    endResearch();
    setLecturePosition({
      branchWorkspace: selectClassroomBranch(branchWorkspace, id, cursor),
      frames: target.frames,
      cursor: target.cursor,
    });
    setPlacementMode(null);
    setAlternateStartColor(target.frames[target.cursor]?.ruleHistory?.nextColor ?? 'BLACK');
    setAlternateBaseCursor(target.cursor);
    setAlternateHint(null);
  };

  const placeAlternateStone = (point: BoardPoint, color = alternateColor) => {
    // Read the store synchronously as a second rapid click can precede React's next render.
    if (store.snapshot().pending !== null) return;
    if (branchWorkspace !== null) {
      const next = followClassroomSourceMove(branchWorkspace, cursor, point, color);
      if (next !== null) {
        store.cancelAi();
        store.clearResearch();
        setLecturePosition((current) =>
          current !== lecturePosition
            ? current
            : { frames: next.frames, cursor: next.cursor, branchWorkspace: next.workspace },
        );
        return;
      }
    }
    const position = classroomRulePosition(frame.stones, boardSize, color, frame.ruleHistory);
    const request = appendClassroomRuleMoves(position, [point]);
    store.clearAiVariation();
    if (aiEnabled) store.cancelAi();
    void store
      .researchClassroom(request)
      .then((accepted) => {
        if (!accepted) return;
        if (branchWorkspace === null) setLocalSgf(null);
        // Keep the textbook opening and its marks when navigating back to the first frame.
        const result = store.snapshot().research;
        if (result === null) return;
        const nextFrame: ClassroomFrame = Object.freeze({
          moveNumber: frame.moveNumber + 1,
          stones: result.stones,
          lastMove: point,
          color,
          ruleHistory: { request, nextColor: result.nextColor },
        });
        const prefix = frames.slice(0, cursor + 1);
        // AI can begin a sequence directly, without choosing the alternating tool first.
        if (cursor === 0) {
          prefix[0] = Object.freeze({
            ...frame,
            ruleHistory: { request: position, nextColor: color },
          });
        }
        const nextFrames = Object.freeze([...prefix, nextFrame]);
        commitClassroomEdit(nextFrames, nextFrames.length - 1);
        if (branchWorkspace === null) setRecordBacked(false);
        setPlacementMode('ALTERNATE');
        // An accepted move advances the sequence; it must not redefine its starting side.
        // Discard a later side override when branching from before that override.
        if (placementMode !== 'ALTERNATE' || cursor < alternateBaseCursor) {
          setAlternateStartColor(color);
          setAlternateBaseCursor(cursor);
        }
      })
      .catch(() => undefined);
  };

  const placeStone = (point: BoardPoint, secondary = false) => {
    if (store.snapshot().pending !== null) return;
    if (placementMode === null) {
      if (branchWorkspace !== null && !secondary) placeAlternateStone(point);
      return;
    }
    if (placementMode === 'ALTERNATE') {
      if (!secondary) placeAlternateStone(point);
      return;
    }
    const apply = secondary ? applyClassroomSecondaryPlacement : applyClassroomPlacement;
    const result = apply(frame.stones, boardSize, point, placementMode, alternateColor);
    if (result === null) return;

    store.clearAiVariation();
    if (aiEnabled) store.cancelAi();
    if (recordBacked && branchWorkspace === null) {
      setRecordBacked(false);
      store.clearResearch();
    }

    const replacement: ClassroomFrame = Object.freeze({
      ...frame,
      stones: result.stones,
      lastMove: null,
      color: null,
      ruleHistory:
        branchWorkspace === null
          ? null
          : {
              request: classroomRulePosition(result.stones, boardSize, alternateColor, null),
              nextColor: alternateColor,
            },
    });
    if (branchWorkspace === null) setLocalSgf(null);
    setTextbookQuestion(null);
    setPracticeProblem(null);
    commitClassroomEdit(Object.freeze([...frames.slice(0, cursor), replacement]), cursor);
  };

  const chooseAlternate = () => {
    const startingColor =
      placementMode === 'ALTERNATE'
        ? nextClassroomAlternateColor(alternateStartColor)
        : branchWorkspace !== null ||
            localSgf !== null ||
            textbookQuestion !== null ||
            practiceProblem !== null
          ? (frame.ruleHistory?.nextColor ??
            localSgf?.initialPlayer ??
            textbookQuestion?.question.currentPlayer ??
            practiceProblem?.initialPosition.currentPlayer ??
            'BLACK')
          : 'BLACK';
    setPlacementMode('ALTERNATE');
    setAlternateStartColor(startingColor);
    setAlternateBaseCursor(cursor);
    setAlternateHint(startingColor === 'BLACK' ? '黑先' : '白先');
    if (cursor === 0 && branchWorkspace === null) {
      // The opening has no previous move from which to recover Black/White first on rewind.
      const opening = Object.freeze({
        ...frame,
        ruleHistory: {
          request: classroomRulePosition(frame.stones, boardSize, startingColor, frame.ruleHistory),
          nextColor: startingColor,
        },
      });
      setFrames(Object.freeze([opening, ...frames.slice(1)]));
    }
  };

  const searchRecords = (event: FormEvent) => {
    event.preventDefault();
    if (searchInput.query.length < 2) return;
    setActiveSearch(searchInput);
    setDetailsOpen(true);
    store.clearError();
    void store.searchDetails(searchInput).catch(() => undefined);
  };

  const loadDetailPage = (page: number) => {
    if (activeSearch === null) return;
    void store.searchDetails({ ...activeSearch, page, pageSize: 10 }).catch(() => undefined);
  };

  const applyLoadedRecord = (
    loaded: FoxKifuRecordData,
    imported: ClassroomSgfData | null = null,
  ) => {
    const initialPlayer = imported?.initialPlayer ?? loaded.frames[1]?.color ?? 'BLACK';
    const workspace = createClassroomBranches(loaded, initialPlayer);
    const nextFrames = workspace.branches[0]!.frames;
    setBoardSize(loaded.record.boardSize);
    setBoardSizeMenuOpen(false);
    setPendingBoardSize(null);
    setLecturePosition({ frames: nextFrames, cursor: 0, branchWorkspace: workspace });
    setPlacementMode(null);
    setAlternateStartColor(initialPlayer);
    setAlternateBaseCursor(0);
    setAlternateHint(null);
    setRecordBacked(imported === null);
    setLocalSgf(imported);
    setTextbookQuestion(null);
    setPracticeProblem(null);
    setMarkerMode('LAST_ONE');
    setResearchBase(null);
    setClassroomResearchPosition(null);
    setVariationMoves([]);
    setResearchCursor(0);
    setAiEnabled(false);
    store.cancelAi();
    store.clearResearch();
    setAnnotations([]);
    setAnnotationOpen(false);
    setBoardCorner(null);
    setBoardZoomMenuOpen(false);
    setDetailsOpen(false);
    setFoxSearchOpen(false);
    foxSearchPanel.resetPosition();
  };

  const loadRecord = (record: FoxKifuRecordSummary) => {
    store.clearResearch();
    setSelectedRecord(record);
    void store
      .loadRecord(record.recordId)
      .then(() => {
        const loaded = store.snapshot().record;
        if (loaded === null || loaded.record.recordId !== record.recordId) return;
        applyLoadedRecord(loaded);
      })
      .catch(() => undefined);
  };

  const selectSgf = (record: ClassroomSgfData) => {
    if (store.snapshot().pending !== null || record === localSgf) return;
    store.clearLoadedRecord();
    setSelectedRecord(null);
    applyLoadedRecord(record, record);
  };

  const chooseSgfFiles = () => {
    if (store.snapshot().pending !== null) return;
    if (sgfInputRef.current !== null) sgfInputRef.current.value = '';
    sgfInputRef.current?.click();
  };

  const uploadSgf = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = '';
    if (files.length === 0) return;
    setSgfImportFailures([]);
    const result = await store.importClassroomSgfs(files);
    if (result === null) return;
    setSgfImportFailures(result.failures);
    if (result.records.length === 0) return;
    setSgfRecords(result.records);
    const first = result.records[0];
    if (first !== undefined) selectSgf(first);
  };

  const resetBoard = (size: BoardSize) => {
    store.clearLoadedRecord();
    setLocalSgf(null);
    setTextbookQuestion(null);
    setPracticeProblem(null);
    setBoardSize(size);
    setBoardSizeMenuOpen(false);
    setPendingBoardSize(null);
    setLecturePosition({ frames: [emptyFrame(size)], cursor: 0, branchWorkspace: null });
    setPlacementMode(studentPractice ? 'ALTERNATE' : null);
    setAlternateStartColor('BLACK');
    setAlternateBaseCursor(0);
    setAlternateHint(null);
    setSelectedRecord(null);
    setRecordBacked(false);
    setMarkerMode('LAST_ONE');
    setResearchBase(null);
    setClassroomResearchPosition(null);
    setVariationMoves([]);
    setResearchCursor(0);
    setAiEnabled(false);
    setAiConnectionOpen(false);
    setAnnotationOpen(false);
    setAnnotations([]);
    setBoardZoomMenuOpen(false);
    setBoardCorner(null);
    setDetailsOpen(false);
    setFoxSearchOpen(false);
    foxSearchPanel.resetPosition();
  };

  const selectTextbookQuestion = (question: EbookQuestion) => {
    if (store.snapshot().pending !== null || textbook === null) return;
    // Only the saved initial position is loaded. Answers stay in the textbook;
    // classroom moves and AI continue to use the authoritative rule service.
    resetBoard(question.size);
    setFrames([classroomTextbookOpening(question)]);
    setAlternateStartColor(question.currentPlayer);
    setTextbookQuestion({ bookId: textbook.id, question });
  };

  const selectPracticeProblem = (problem: StudentProblemSnapshot) => {
    if (store.snapshot().pending !== null) return;
    const { size, stones, currentPlayer } = problem.initialPosition;
    resetBoard(size);
    setFrames([
      {
        moveNumber: 0,
        stones: [...stones],
        lastMove: null,
        color: null,
        ruleHistory: classroomRecordHistory(size, stones, currentPlayer, []),
      },
    ]);
    setAlternateStartColor(currentPlayer);
    setPlacementMode('ALTERNATE');
    setPracticeProblem(problem);
    setCaptureMessage('');
  };

  const captureBoard = async () => {
    const svg = boardStageRef.current?.querySelector<SVGSVGElement>('svg.go-board');
    if (!svg || capturing) return;
    setCapturing(true);
    setCaptureMessage('');
    try {
      await downloadBoardJpeg(
        svg,
        foxBoardCaptureFilename(
          studentPractice ? 'student-practice' : 'classroom-lecture',
          researching ? researchCursor : cursor,
        ),
        boardStageRef.current?.querySelector<SVGSVGElement>('svg.review-annotation-layer'),
      );
      setCaptureMessage('当前棋盘已保存为 JPG 图片。');
    } catch (error: unknown) {
      setCaptureMessage(userErrorMessage(error, '棋盘截图失败，请重试。'));
    } finally {
      setCapturing(false);
    }
  };

  const chooseBoardSize = (size: BoardSize) => {
    // Do not let an in-flight record/research response restore a board of the old size.
    if (store.snapshot().pending !== null) return;
    if (size === boardSize) {
      setBoardSizeMenuOpen(false);
      setPendingBoardSize(null);
      boardSizeButtonRef.current?.focus();
      return;
    }
    const hasBoardContent =
      selectedRecord !== null ||
      frames.length > 1 ||
      frames.some((item) => item.stones.some((stone) => stone !== null)) ||
      variationMoves.length > 0 ||
      annotations.length > 0;
    if (hasBoardContent) {
      setPendingBoardSize(size);
      return;
    }
    resetBoard(size);
    boardSizeButtonRef.current?.focus();
  };

  const rememberNotesSelection = () => {
    const editor = notesEditorRef.current;
    const selection = document.getSelection();
    if (editor === null || selection === null || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) {
      notesSelectionRef.current = range.cloneRange();
      setNoteBold(noteRangeIsBold(editor, noteFormatRange(editor, range)));
    }
  };

  const applyNotesFormat = (format: ClassroomNoteFormat) => {
    const editor = notesEditorRef.current;
    if (editor === null) return;
    const selection = document.getSelection();
    if (selection === null) return;

    const range = noteFormatRange(editor, notesSelectionRef.current);
    if (range.collapsed) return;
    const formattedRange = formatNoteRange(editor, range, format);
    editor.focus({ preventScroll: true });
    selection.removeAllRanges();
    selection.addRange(formattedRange);
    notesSelectionRef.current = formattedRange.cloneRange();
    setNoteBold(noteRangeIsBold(editor, formattedRange));
    setNotes(editor.innerHTML);
  };

  const cycleMarkerMode = () => {
    const current = MARKER_MODES.indexOf(markerMode);
    setMarkerMode(MARKER_MODES[(current + 1) % MARKER_MODES.length] ?? 'LAST_ONE');
  };

  const endResearch = () => {
    store.cancelAi();
    store.clearResearch();
    setResearchBase(null);
    setClassroomResearchPosition(null);
    setVariationMoves([]);
    setResearchCursor(0);
  };

  const placeResearch = (point: BoardPoint) => {
    if (researchBase === null || state.pending !== null) {
      return;
    }
    const next = branchResearchVariation(variationMoves, researchCursor, point);
    store.cancelAi();
    if (classroomResearchPosition !== null) {
      void store
        .researchClassroom(appendClassroomRuleMoves(classroomResearchPosition, next))
        .then((accepted) => {
          if (!accepted) return;
          setVariationMoves(next);
          setResearchCursor(next.length);
        })
        .catch(() => undefined);
      return;
    }
    if (selectedRecordId === null) return;
    void store
      .research(selectedRecordId, researchBase, next)
      .then(() => {
        setVariationMoves(next);
        setResearchCursor(next.length);
      })
      .catch(() => undefined);
  };

  const navigateResearch = (nextCursor: number) => {
    if (researchBase === null || state.pending !== null) {
      return;
    }
    const target = Math.max(0, Math.min(variationMoves.length, Math.trunc(nextCursor)));
    if (target === researchCursor) return;
    store.cancelAi();
    if (classroomResearchPosition !== null) {
      void store
        .researchClassroom(
          appendClassroomRuleMoves(
            classroomResearchPosition,
            researchVariationPrefix(variationMoves, target),
          ),
        )
        .then((accepted) => {
          if (accepted) setResearchCursor(target);
        })
        .catch(() => undefined);
      return;
    }
    if (selectedRecordId === null) return;
    if (target === 0) {
      store.clearResearch();
      setResearchCursor(0);
      return;
    }
    void store
      .research(selectedRecordId, researchBase, researchVariationPrefix(variationMoves, target))
      .then(() => setResearchCursor(target))
      .catch(() => undefined);
  };

  const navigateLecture = (action: 'PREVIOUS' | 'NEXT' | 'FIRST' | 'LAST' | number) => {
    if (store.snapshot().pending !== null) return;
    store.clearAiVariation();
    if (aiEnabled) store.cancelAi();
    // After navigation, imported frames own the turn again; an old manual side choice
    // at the trial's origin must not leak into the restored source position.
    if (branchWorkspace !== null) setAlternateBaseCursor(-1);
    const target = (position: number, last: number) =>
      typeof action === 'number'
        ? action
        : action === 'FIRST'
          ? 0
          : action === 'LAST'
            ? last
            : position + (action === 'PREVIOUS' ? -1 : 1);
    if (researching) {
      navigateResearch(target(researchCursor, variationMoves.length));
      return;
    }
    setLecturePosition((current) => {
      if (current.branchWorkspace === null)
        return {
          ...current,
          cursor: Math.max(
            0,
            Math.min(current.frames.length - 1, target(current.cursor, current.frames.length - 1)),
          ),
        };
      const next =
        action === 'PREVIOUS'
          ? previousClassroomBranch(current.branchWorkspace, current.cursor)
          : seekClassroomBranch(
              current.branchWorkspace,
              current.cursor,
              target(current.cursor, current.frames.length - 1),
              action === 'FIRST',
            );
      return { frames: next.frames, cursor: next.cursor, branchWorkspace: next.workspace };
    });
  };

  useBoardWheelNavigation(boardStageRef, {
    enabled: true,
    blocked:
      annotationOpen ||
      detailsOpen ||
      aiConnectionOpen ||
      boardSizeMenuOpen ||
      state.pending !== null,
    canPrevious: !previousUnavailable,
    canNext: researching ? researchCursor < variationMoves.length : cursor < totalMoves,
    context: `${boardSize}:${selectedRecordId ?? 'free'}:${researchBase ?? 'main'}:${branchWorkspace?.activeId ?? 'free'}:${placementMode}`,
    onStep: (direction) => {
      navigateLecture(direction < 0 ? 'PREVIOUS' : 'NEXT');
    },
  });

  const usePreviousMove = () => {
    navigateLecture('PREVIOUS');
  };

  const handleBoardContextMenu = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (branchWorkspace === null && placementMode === null && !researching) return;
    event.preventDefault();
    if (annotationOpen || detailsOpen || state.pending !== null) return;
    if (branchWorkspace !== null || placementMode === 'ALTERNATE' || researching) usePreviousMove();
  };

  const handlePointContextMenu = (point: BoardPoint, event: ReactMouseEvent<Element>) => {
    if (branchWorkspace !== null) {
      event.preventDefault();
      event.stopPropagation();
      if (!annotationOpen && !detailsOpen && state.aiVariation === null) usePreviousMove();
      return;
    }
    if (researching || placementMode === null || placementMode === 'ALTERNATE') return;
    event.preventDefault();
    event.stopPropagation();
    if (annotationOpen || detailsOpen || state.aiVariation !== null) return;
    placeStone(point, true);
  };

  const toggleResearch = () => {
    if (researching) {
      endResearch();
      return;
    }
    if (!researchAvailable || store.snapshot().pending !== null) return;
    if (branchWorkspace !== null && activeBranch !== undefined) {
      if (activeBranch.kind === 'VARIATION') switchClassroomBranch(activeBranch.originId);
      else {
        store.cancelAi();
        store.clearResearch();
        const workspace = startClassroomVariation(branchWorkspace, cursor);
        setLecturePosition({
          frames: frames.slice(0, cursor + 1),
          cursor,
          branchWorkspace: workspace,
        });
        setPlacementMode('ALTERNATE');
      }
      return;
    }
    store.cancelAi();
    store.clearAiVariation();
    store.clearResearch();
    if (
      placementMode === 'ALTERNATE' ||
      localSgf !== null ||
      textbookQuestion !== null ||
      practiceProblem !== null
    ) {
      const position = classroomRulePosition(
        frame.stones,
        boardSize,
        alternateColor,
        frame.ruleHistory,
      );
      void store
        .researchClassroom(position)
        .then((accepted) => {
          if (!accepted) return;
          setClassroomResearchPosition(position);
          setResearchBase(cursor);
          setVariationMoves([]);
          setResearchCursor(0);
        })
        .catch(() => undefined);
      return;
    }
    setPlacementMode(null);
    setClassroomResearchPosition(null);
    setResearchBase(cursor);
    setVariationMoves([]);
    setResearchCursor(0);
    store.clearResearch();
  };

  const selectAiCandidate = (point: BoardPoint) => {
    if (state.pending !== null || state.aiPending) return;
    if (researching) {
      placeResearch(point);
      return;
    }
    if (branchWorkspace !== null) {
      placeAlternateStone(point);
      return;
    }
    if (!recordToolsAvailable || selectedRecordId === null || placementMode === 'ALTERNATE') {
      const candidateColor =
        placementMode === 'ALTERNATE' ||
        localSgf !== null ||
        textbookQuestion !== null ||
        practiceProblem !== null
          ? alternateColor
          : alternateStartColor;
      placeAlternateStone(point, candidateColor);
      return;
    }
    const next = [point];
    store.cancelAi();
    setPlacementMode(null);
    setResearchBase(cursor);
    setVariationMoves([]);
    setResearchCursor(0);
    store.clearResearch();
    void store
      .research(selectedRecordId, cursor, next)
      .then(() => {
        setVariationMoves(next);
        setResearchCursor(1);
      })
      .catch(() => endResearch());
  };

  const checkAiConnection = async () => {
    setAiChecking(true);
    await store.refreshAiStatus();
    setAiChecking(false);
    if (store.snapshot().aiAvailable === true) {
      setAiConnectionOpen(false);
      setAiEnabled(true);
    }
  };

  const toggleAi = async () => {
    if (aiEnabled) {
      setAiEnabled(false);
      store.cancelAi();
      return;
    }
    if (state.aiAvailable === true) {
      if (recordToolsAvailable && placementMode !== 'ALTERNATE') setPlacementMode(null);
      setAiEnabled(true);
      return;
    }
    setAiConnectionOpen(true);
    await checkAiConnection();
  };

  const addStroke = (stroke: ReviewBrushStroke) => {
    setAnnotations((current) => [...current, stroke]);
  };

  const addPointAnnotation = (point: BoardPoint) => {
    if (annotationTool === 'BRUSH') return;
    setAnnotations((current) =>
      toggleReviewPointAnnotation(current, point, annotationTool, annotationColor),
    );
  };

  const toggleAnnotationPanel = () => {
    setSgfPanelState((current) => (current === 'OPEN' ? 'MINIMIZED' : current));
    setTextbookPanelOpen(false);
    setAnnotationOpen((open) => !open);
  };

  const minimizeSgfPanel = () => {
    setSgfPanelState('MINIMIZED');
    requestAnimationFrame(() => sgfRestoreButtonRef.current?.focus());
  };

  const toggleFullscreen = async () => {
    if (document.fullscreenElement !== null) {
      await document.exitFullscreen().catch(() => undefined);
      setImmersive(false);
      return;
    }
    const page = pageRef.current;
    if (page?.requestFullscreen !== undefined) {
      await page.requestFullscreen().catch(() => setImmersive(true));
      return;
    }
    setImmersive(true);
  };

  const AnnotationPanel = studio ? ClassroomAnnotationToolbar : ReviewAnnotationPanel;
  const annotationPanel = annotationOpen ? (
    <AnnotationPanel
      tool={annotationTool}
      color={annotationColor}
      width={annotationWidth}
      annotationCount={annotations.length}
      sharingStage={aiEnabled}
      draggable={!studio}
      onTool={setAnnotationTool}
      onColor={setAnnotationColor}
      onWidth={setAnnotationWidth}
      onUndo={() => setAnnotations((current) => current.slice(0, -1))}
      onClear={() => setAnnotations([])}
      onClose={() => setAnnotationOpen(false)}
    />
  ) : null;

  const lectureAnnotationProps = {
    size: boardSize,
    open: annotationOpen,
    tool: annotationTool,
    color: annotationColor,
    width: annotationWidth,
    annotations,
    viewport: boardViewport,
    onStroke: addStroke,
    onPoint: addPointAnnotation,
  } satisfies ComponentProps<typeof ReviewAnnotationLayer>;
  const lectureBoardProps = {
    size: boardSize,
    stones: displayedStones,
    lastMove: state.aiVariation === null && moveLabels.length === 0 ? frame.lastMove : null,
    stoneLabels: displayedLabels,
    aiCandidates: state.aiVariation === null ? aiCandidates : [],
    viewport: boardViewport,
    initialAnnotations:
      cursor === 0 && !researching
        ? (practiceProblem?.initialAnnotations ??
          localSgf?.annotations ??
          (textbookQuestion !== null
            ? classroomTextbookQuestionAnnotations(textbookQuestion.question)
            : []))
        : [],
    coordinateEdges: boardCoordinateEdges,
    onContextMenu: handleBoardContextMenu,
    onPointContextMenu: handlePointContextMenu,
    ...(annotationOpen || state.aiVariation !== null
      ? {}
      : researching
        ? {
            isActionable: (_point: BoardPoint, stone: StoneColor | null) =>
              stone === null && state.pending === null,
            onPoint: placeResearch,
          }
        : placementMode !== null || branchWorkspace !== null
          ? {
              isActionable: (_point: BoardPoint, stone: StoneColor | null) =>
                state.pending === null &&
                ((placementMode !== null && placementMode !== 'ALTERNATE') || stone === null),
              onPoint: placeStone,
            }
          : aiEnabled && aiCandidates.length > 0
            ? {
                isActionable: (point: BoardPoint, stone: StoneColor | null) =>
                  stone === null && aiCandidateKeys.has(pointKey(point)),
                onPoint: selectAiCandidate,
              }
            : {}),
  } satisfies ComponentProps<typeof BoardSurface>;

  return (
    <main
      ref={pageRef}
      className={`board-workspace board-workspace--teacher fox-kifu-page classroom-lecture-page${studio ? ' classroom-studio' : ''}${studentPractice ? ' student-practice-page' : ''}${immersive ? ' fox-kifu-page--immersive' : ''}`}
    >
      {studio ? (
        <input
          ref={sgfInputRef}
          type="file"
          multiple
          accept=".sgf,application/x-go-sgf"
          hidden
          aria-label="上传SGF棋谱文件"
          onChange={(event) => void uploadSgf(event)}
        />
      ) : null}
      <ClassroomWorkspace swapped={studio && panelsSwapped}>
        {!studio ? (
          <aside key="rail" className="fox-kifu-move-rail" aria-label="课堂棋盘手数导航">
            <button
              className="back-button fox-kifu-back return-button"
              type="button"
              onClick={onBack}
            >
              ← 返回大厅
            </button>
            {(['BLACK', 'WHITE'] as const).map((color) => {
              const player = playerName(selectedRecord, color);
              return (
                <div className="fox-kifu-player-badge" key={color}>
                  <span className={`stone-preview stone-preview--${color.toLowerCase()}`} />
                  <strong>{player.name}</strong>
                  <small>{studentPractice ? '自由摆棋' : player.rank}</small>
                </div>
              );
            })}
            <div className="fox-kifu-vertical-progress">
              <strong>{frame.moveNumber}</strong>
              <VerticalMoveSlider
                max={totalMoves}
                value={cursor}
                disabled={progressUnavailable || positionPending}
                busy={!progressUnavailable && positionPending}
                onChange={navigateLecture}
              />
              <small>/ {totalMoves}</small>
            </div>
            <BoardMoveProgress
              max={totalMoves}
              value={cursor}
              disabled={progressUnavailable || positionPending}
              busy={!progressUnavailable && positionPending}
              onChange={navigateLecture}
            />
          </aside>
        ) : null}

        <section key="board" className="fox-kifu-board-panel">
          <div ref={boardStageRef} className="fox-kifu-board-stage">
            <div
              className={studio ? 'studio-board-frame teacher-board-frame' : 'classroom-board-wrap'}
            >
              <BoardSurface
                {...lectureBoardProps}
                boardAppearance={studentPractice ? 'wood' : 'classic'}
                stoneAppearance={studio || studentPractice ? 'shell' : 'classic'}
                annotationLayer={<ReviewAnnotationLayer {...lectureAnnotationProps} />}
              />
            </div>
            {detailsOpen ? (
              <FoxKifuRecordDetailsDialog
                state={state.detailSearch}
                pending={state.pending}
                page={detailPage}
                totalPages={detailPages}
                selectedId={selectedRecordId}
                onClose={() => setDetailsOpen(false)}
                onPage={loadDetailPage}
                onSelect={loadRecord}
              />
            ) : null}
            {aiConnectionOpen ? (
              <section
                className="fox-kifu-details-backdrop"
                role="presentation"
                onMouseDown={(event) => {
                  if (event.currentTarget === event.target) setAiConnectionOpen(false);
                }}
              >
                <div
                  className="fox-kifu-ai-dialog"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="classroom-ai-dialog-title"
                >
                  <header>
                    <span>
                      <small>课堂棋盘分析</small>
                      <h2 id="classroom-ai-dialog-title">智子云 AI 算力</h2>
                    </span>
                    <button type="button" onClick={() => setAiConnectionOpen(false)}>
                      关闭
                    </button>
                  </header>
                  <div className="fox-kifu-ai-connection-status" aria-live="polite">
                    <i className={state.aiAvailable === true ? 'is-ready' : ''} />
                    <span>
                      <strong>
                        {aiChecking
                          ? '正在检测连接…'
                          : state.aiAvailable === true
                            ? '算力任务服务可用'
                            : state.aiServiceEnabled === false
                              ? '平台 AI 算力总开关未启用'
                              : state.aiAuthorized === false
                                ? '当前账号未开通 AI 算力'
                                : state.aiConfigured === false
                                  ? '教师尚未配置智子云算力'
                                  : '智子云算力暂时离线'}
                      </strong>
                      <small>
                        {state.aiAvailable === true
                          ? recordToolsAvailable
                            ? '已载入棋谱，将分析当前手数的棋谱局面。'
                            : '无需载入棋谱，可直接分析当前自由摆棋局面。'
                          : '算力连接和使用权限由教师端 AI 管理维护。'}
                      </small>
                    </span>
                  </div>
                  <AiAccountSelector
                    state={state}
                    onSelect={(id) => store.selectAiAccount(id)}
                    onSaveDefault={() => store.saveAiDefault()}
                  />
                  {state.aiProfile !== null ? (
                    <dl className="fox-kifu-ai-profile">
                      <div>
                        <dt>算力模式</dt>
                        <dd>{ZHIZI_COMPUTE_MODE_LABELS[state.aiProfile.computeMode]}</dd>
                      </div>
                      <div>
                        <dt>档位 / 权重</dt>
                        <dd>
                          {state.aiProfile.exclusiveTier === null
                            ? '共享档'
                            : ZHIZI_EXCLUSIVE_TIER_LABELS[state.aiProfile.exclusiveTier]}{' '}
                          · {zhiziWeightLabel(state.aiProfile.engineWeight)}
                        </dd>
                      </div>
                      <div>
                        <dt>分析参数</dt>
                        <dd>
                          {state.aiProfile.maxVisits === 0
                            ? '不封顶'
                            : `${state.aiProfile.maxVisits} 计算量`}{' '}
                          · {state.aiProfile.recommendationCount} 个推荐点
                        </dd>
                      </div>
                    </dl>
                  ) : null}
                  <footer>
                    <button type="button" onClick={() => setAiConnectionOpen(false)}>
                      关闭
                    </button>
                    <button
                      className="primary-button"
                      type="button"
                      disabled={aiChecking}
                      onClick={() => void checkAiConnection()}
                    >
                      {aiChecking ? '正在检测…' : '重新检测并连接'}
                    </button>
                  </footer>
                </div>
              </section>
            ) : null}
            {!studio && boardZoomMenuOpen ? (
              <section
                className="review-board-zoom-menu"
                role="dialog"
                aria-label="选择棋盘放大区域"
              >
                <header>
                  <strong>选择放大区域</strong>
                  <button type="button" onClick={() => setBoardZoomMenuOpen(false)}>
                    ×
                  </button>
                </header>
                <div>
                  {(Object.keys(BOARD_CORNER_LABELS) as HomeworkBoardCorner[]).map((corner) => (
                    <button
                      key={corner}
                      type="button"
                      className={boardCorner === corner ? 'is-active' : ''}
                      onClick={() => {
                        setBoardCorner(corner);
                        setBoardZoomMenuOpen(false);
                      }}
                    >
                      {BOARD_CORNER_LABELS[corner]}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </section>

        <aside key="tools" className="fox-kifu-data-panel classroom-lecture-sidebar">
          <header className="fox-kifu-data-title">
            {studio ? (
              <button className="studio-back" type="button" onClick={onBack} aria-label="返回大厅">
                <ArrowLeftIcon aria-hidden="true" />
                <span>返回</span>
              </button>
            ) : null}
            {studio && lectureRecords ? (
              <button
                type="button"
                className="studio-record-toggle"
                aria-pressed={recordMode}
                disabled={!recordMode && (positionPending || state.aiVariation !== null)}
                onClick={() => setRecordMode((current) => !current)}
              >
                记录
              </button>
            ) : null}
            <h1>{studentPractice ? '摆棋' : '大盘讲解'}</h1>
            {studio ? (
              <div className="classroom-board-actions studio-board-size" ref={boardSizeControlRef}>
                <ClassroomControlButton
                  ref={boardSizeButtonRef}
                  type="button"
                  busy={positionPending}
                  disabled={state.pending !== null && !positionPending}
                  aria-expanded={boardSizeMenuOpen}
                  aria-label="切换棋盘"
                  title={`当前为${boardSize}路棋盘`}
                  onClick={() => {
                    setPendingBoardSize(null);
                    setBoardSizeMenuOpen(!boardSizeMenuOpen);
                  }}
                >
                  {boardSize}路<ChevronDownIcon aria-hidden="true" />
                </ClassroomControlButton>
                {boardSizeMenuOpen ? (
                  <ClassroomBoardSizeMenu
                    currentSize={boardSize}
                    disabled={state.pending !== null}
                    onSelect={chooseBoardSize}
                    pendingSize={pendingBoardSize}
                    onCancel={() => {
                      setPendingBoardSize(null);
                      setBoardSizeMenuOpen(false);
                      boardSizeButtonRef.current?.focus();
                    }}
                    onConfirm={() => {
                      if (pendingBoardSize === null || store.snapshot().pending !== null) return;
                      resetBoard(pendingBoardSize);
                      boardSizeButtonRef.current?.focus();
                    }}
                  />
                ) : null}
              </div>
            ) : null}
            {studio ? (
              <button
                ref={panelSwapButtonRef}
                className="studio-panel-swap"
                type="button"
                aria-label="左右切换"
                aria-pressed={panelsSwapped}
                title={panelsSwapped ? '左右切换：棋盘移至左侧' : '左右切换：棋盘移至右侧'}
                onClick={() => {
                  restoreSwapFocusRef.current = true;
                  setPanelsSwapped((swapped) => !swapped);
                }}
              >
                <span aria-hidden="true">⇋</span>
              </button>
            ) : null}
            {studio ? (
              <button
                className="studio-annotation-button"
                type="button"
                title="添加标记"
                aria-label="添加标记"
                aria-pressed={annotationOpen}
                aria-expanded={annotationOpen}
                onClick={toggleAnnotationPanel}
              >
                <Pencil2Icon aria-hidden="true" />
              </button>
            ) : null}
            {studio ? (
              <button
                className="studio-capture-button"
                type="button"
                title="保存当前棋盘为 JPG 图片"
                aria-label="截屏"
                aria-busy={capturing}
                disabled={capturing}
                onClick={() => void captureBoard()}
              >
                {capturing ? '保存中…' : '截屏'}
              </button>
            ) : null}
            {studio && sgfPanelState === 'MINIMIZED' ? (
              <button
                ref={sgfRestoreButtonRef}
                className="studio-sgf-restore"
                type="button"
                aria-label="恢复SGF文件窗口"
                aria-haspopup="dialog"
                aria-expanded={false}
                title="恢复SGF文件窗口"
                onClick={() => {
                  setAnnotationOpen(false);
                  setTextbookPanelOpen(false);
                  setSgfPanelState('OPEN');
                }}
              >
                SGF
              </button>
            ) : null}
            {studio && textbook !== null && !textbookPanelOpen ? (
              <button
                ref={textbookRestoreButtonRef}
                className="studio-sgf-restore"
                type="button"
                aria-label="恢复教材题目列表"
                onClick={() => {
                  setAnnotationOpen(false);
                  setSgfPanelState((current) => (current === 'OPEN' ? 'MINIMIZED' : current));
                  setTextbookPanelOpen(true);
                }}
              >
                教材
              </button>
            ) : null}
            <button
              className="secondary-button fullscreen-action"
              type="button"
              onClick={() => void toggleFullscreen()}
            >
              {studio ? (
                <>
                  {immersive ? (
                    <ExitFullScreenIcon aria-hidden="true" />
                  ) : (
                    <EnterFullScreenIcon aria-hidden="true" />
                  )}
                  <span className="sr-only">{immersive ? '退出全屏' : '棋盘全屏'}</span>
                </>
              ) : immersive ? (
                '退出全屏'
              ) : (
                '棋盘全屏'
              )}
            </button>
            {studio ? annotationPanel : null}
            {studio && sgfPanelState === 'OPEN' ? (
              <ClassroomSgfPanel
                records={sgfRecords}
                selected={localSgf}
                failures={sgfImportFailures}
                busy={state.pending !== null}
                uploading={state.pending === 'SGF_IMPORT'}
                onSelect={selectSgf}
                onUpload={chooseSgfFiles}
                onClose={minimizeSgfPanel}
              />
            ) : null}
            {studio && textbookPanelOpen && textbook !== null ? (
              <ClassroomTextbookPanel
                textbook={textbook}
                selectedId={
                  textbookQuestion?.bookId === textbook.id ? textbookQuestion.question.id : null
                }
                busy={state.pending !== null}
                onSelect={selectTextbookQuestion}
                onChoose={() => setTextbookPickerOpen(true)}
                onClose={() => {
                  setTextbookPanelOpen(false);
                  requestAnimationFrame(() => textbookRestoreButtonRef.current?.focus());
                }}
              />
            ) : null}
          </header>
          <div className="workspace-tool-dock" aria-label="课堂棋盘工具区">
            {aiEnabled ? (
              <AiCandidatePanel
                preserveWidth={studio}
                account={state.aiAnalysis?.account ?? selectedAccount(state)}
                onChangeAccount={() => setAiConnectionOpen(true)}
                candidates={aiCandidates}
                selectedRank={state.aiVariation?.candidateRank ?? null}
                variationPending={state.aiVariationPending}
                sharingStage={annotationOpen}
                onSelect={(rank) => {
                  if (state.aiVariation?.candidateRank === rank) {
                    store.clearAiVariation();
                    return;
                  }
                  const analysisId = state.aiAnalysis?.analysisId;
                  if (analysisId === undefined) return;
                  void store.loadAiVariation(analysisId, rank).catch(() => undefined);
                }}
              />
            ) : null}
            {!studio ? annotationPanel : null}
            {foxSearchOpen ? (
              <aside
                ref={foxSearchPanel.panelRef}
                className="classroom-fox-search-panel"
                style={foxSearchPanel.style}
                aria-label="野狐棋谱查询"
              >
                <header {...foxSearchPanel.dragHandlers}>
                  <strong>野狐棋谱</strong>
                  <span>拖动此处移动</span>
                  {foxSearchPanel.floating ? (
                    <button type="button" onClick={foxSearchPanel.resetPosition}>
                      归位
                    </button>
                  ) : null}
                  <button
                    type="button"
                    aria-label="关闭野狐棋谱查询"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => {
                      foxSearchPanel.resetPosition();
                      setFoxSearchOpen(false);
                    }}
                  >
                    关闭
                  </button>
                </header>
                <form onSubmit={searchRecords}>
                  <label>
                    野狐 ID
                    <input
                      value={query}
                      maxLength={64}
                      placeholder="输入野狐昵称或 UID"
                      onChange={(event) => setQuery(event.currentTarget.value)}
                    />
                  </label>
                  <div className="fox-kifu-date-row">
                    <label>
                      开始日期
                      <input
                        type="date"
                        value={from}
                        onChange={(event) => setFrom(event.currentTarget.value)}
                      />
                    </label>
                    <label>
                      结束日期
                      <input
                        type="date"
                        value={to}
                        onChange={(event) => setTo(event.currentTarget.value)}
                      />
                    </label>
                  </div>
                  <button
                    className="primary-button"
                    type="submit"
                    disabled={state.pending !== null || searchInput.query.length < 2}
                  >
                    {state.pending === 'DETAILS' ? '正在查询…' : '查询棋谱列表'}
                  </button>
                </form>
                {state.error === null ? null : (
                  <p className="message message--error" role="alert">
                    {state.error}
                  </p>
                )}
                {selectedRecord === null ? null : (
                  <p className="classroom-selected-record">
                    已载入：{selectedRecord.black.nickname} 对 {selectedRecord.white.nickname}
                  </p>
                )}
              </aside>
            ) : null}
          </div>
          <div className="classroom-sidebar-content">
            <section
              className="fox-kifu-panel-card classroom-notes-card"
              hidden={studio && recordMode && !!lectureRecords}
            >
              <div className="fox-kifu-section-heading">
                <span>
                  <strong>{noteTitle}</strong>
                </span>
                <div
                  className="classroom-note-toolbar"
                  role="toolbar"
                  aria-label={`${noteTitle}文字格式`}
                  onPointerDownCapture={rememberNotesSelection}
                >
                  <button
                    className="classroom-note-tool classroom-note-tool--bold"
                    type="button"
                    aria-label="加粗"
                    aria-pressed={noteBold}
                    title="加粗"
                    onPointerDown={(event) => {
                      rememberNotesSelection();
                      event.preventDefault();
                    }}
                    onClick={() => {
                      const editor = notesEditorRef.current;
                      if (editor === null) return;
                      const bold = noteRangeIsBold(
                        editor,
                        noteFormatRange(editor, notesSelectionRef.current),
                      );
                      applyNotesFormat({ fontWeight: bold ? '400' : '700' });
                    }}
                  >
                    B
                  </button>
                  <div className="classroom-note-dropdown">
                    <button
                      className="classroom-note-tool classroom-note-tool--text-color"
                      type="button"
                      aria-label="字体颜色"
                      aria-expanded={noteTextColorMenuOpen}
                      title="字体颜色"
                      onPointerDown={(event) => {
                        rememberNotesSelection();
                        event.preventDefault();
                      }}
                      onClick={() => {
                        setNoteHighlightMenuOpen(false);
                        setNoteTextColorMenuOpen((open) => !open);
                      }}
                    >
                      <span aria-hidden="true">A</span>
                      <i aria-hidden="true" style={{ backgroundColor: noteTextColor }} />
                    </button>
                    {noteTextColorMenuOpen ? (
                      <div className="classroom-note-palette" role="menu" aria-label="选择字体颜色">
                        {NOTE_TEXT_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            role="menuitem"
                            onPointerDown={(event) => event.preventDefault()}
                            onClick={() => {
                              setNoteTextColor(color.value);
                              applyNotesFormat({ color: color.value });
                              setNoteTextColorMenuOpen(false);
                            }}
                          >
                            <i aria-hidden="true" style={{ backgroundColor: color.value }} />
                            {color.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="classroom-note-dropdown">
                    <button
                      className="classroom-note-tool classroom-note-tool--highlight"
                      type="button"
                      aria-label="文本突出显示颜色"
                      aria-expanded={noteHighlightMenuOpen}
                      title="文本突出显示颜色"
                      onPointerDown={(event) => {
                        rememberNotesSelection();
                        event.preventDefault();
                      }}
                      onClick={() => {
                        setNoteTextColorMenuOpen(false);
                        setNoteHighlightMenuOpen((open) => !open);
                      }}
                    >
                      <span aria-hidden="true" style={{ backgroundColor: noteHighlightColor }}>
                        A
                      </span>
                    </button>
                    {noteHighlightMenuOpen ? (
                      <div
                        className="classroom-note-palette"
                        role="menu"
                        aria-label="选择突出显示颜色"
                      >
                        {NOTE_HIGHLIGHT_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            role="menuitem"
                            onPointerDown={(event) => event.preventDefault()}
                            onClick={() => {
                              setNoteHighlightColor(color.value);
                              applyNotesFormat({ backgroundColor: color.value });
                              setNoteHighlightMenuOpen(false);
                            }}
                          >
                            <i aria-hidden="true" style={{ backgroundColor: color.value }} />
                            {color.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <label className="classroom-note-size" title="字体大小">
                    <span className="sr-only">字体大小</span>
                    <select
                      aria-label="字体大小"
                      value={noteFontSize}
                      onPointerDown={rememberNotesSelection}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        setNoteFontSize(value);
                        applyNotesFormat({ fontSize: `${value}px` });
                      }}
                    >
                      {CLASSROOM_NOTE_FONT_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                  {studio ? (
                    <button
                      type="button"
                      className="classroom-note-tool classroom-note-share-button"
                      aria-label="分享课堂笔记"
                      aria-haspopup="dialog"
                      disabled={
                        !notes
                          .replace(/<[^>]*>/g, '')
                          .replace(/&nbsp;|\u00a0/g, ' ')
                          .trim()
                      }
                      title="生成学习便签并保存图片"
                      onClick={() => {
                        const editor = notesEditorRef.current;
                        if (!editor) return;
                        setNoteShareError(null);
                        try {
                          const snapshot = readClassroomNote(editor);
                          if (snapshot.length === 0) {
                            setNoteShareError('请先填写课堂笔记，再生成分享便签。');
                            return;
                          }
                          setNoteTextColorMenuOpen(false);
                          setNoteHighlightMenuOpen(false);
                          setNoteShare(snapshot);
                        } catch (error: unknown) {
                          setNoteShareError(userErrorMessage(error, '无法读取笔记，请重试。'));
                        }
                      }}
                    >
                      <Share1Icon aria-hidden="true" />
                      分享
                    </button>
                  ) : null}
                </div>
              </div>
              {noteShareError ? (
                <p className="classroom-note-share-error" role="alert">
                  {noteShareError}
                </p>
              ) : null}
              <div
                ref={notesEditorRef}
                className="classroom-notes-editor"
                role="textbox"
                aria-label={`${noteTitle}内容`}
                aria-multiline="true"
                contentEditable
                data-placeholder={
                  studentPractice ? '点击这里记录思路与心得…' : '点击这里记录课堂要点…'
                }
                suppressContentEditableWarning
                spellCheck={false}
                onInput={(event) => {
                  const editor = event.currentTarget;
                  if ((editor.textContent ?? '').length > 8_000) {
                    editor.innerHTML = notes;
                    return;
                  }
                  setNotes(editor.innerHTML);
                  setNoteShareError(null);
                  rememberNotesSelection();
                }}
                onKeyUp={rememberNotesSelection}
                onPointerUp={rememberNotesSelection}
                onBlur={rememberNotesSelection}
              />
            </section>
            {lectureRecords && lectureRecordScope ? (
              <LectureRecordsPanel
                key={lectureRecordScope}
                api={lectureRecords}
                scope={lectureRecordScope}
                active={studio && recordMode}
                available={!positionPending && state.aiVariation === null}
                onClose={() => setRecordMode(false)}
                capture={() => {
                  if (positionPending || state.aiVariation !== null)
                    throw new Error('请等待局面载入完成或退出 AI 变化预览后记录。');
                  const source = localSgf
                    ? {
                        kind: 'SGF',
                        id: localSgf.record.recordId,
                        version: localSgf.record.recordId,
                        name: localSgf.filename,
                        gameId: null,
                      }
                    : textbookQuestion
                      ? {
                          kind: 'TEXTBOOK',
                          id: `${textbookQuestion.bookId}:${textbookQuestion.question.id}`,
                          version: String(textbookQuestion.question.revision),
                          name:
                            textbookQuestion.question.title ||
                            `教材第 ${textbookQuestion.question.number} 题`,
                          gameId: null,
                        }
                      : recordBacked && selectedRecord
                        ? {
                            kind: 'FOX',
                            id: selectedRecord.recordId,
                            version: selectedRecord.playedAt,
                            name: `${selectedRecord.black.nickname} 对 ${selectedRecord.white.nickname}`,
                            gameId: selectedRecord.recordId,
                          }
                        : {
                            kind: 'DIAGRAM',
                            id: 'classroom-diagram',
                            version: '1',
                            name: '课堂摆棋',
                            gameId: null,
                          };
                  return lectureSnapshotSchema.parse({
                    size: boardSize,
                    stones: baseStones,
                    nextColor: researchNextColor,
                    source: { ...source, name: source.name.slice(0, 240) },
                    baseMove:
                      researchBase ??
                      (activeBranch?.kind === 'VARIATION'
                        ? Math.min(cursor, activeBranch.originCursor)
                        : frame.moveNumber),
                    moveNumber: frame.moveNumber,
                    variation: researching
                      ? activeVariationMoves
                      : activeBranch?.kind === 'VARIATION'
                        ? frames
                            .slice(activeBranch.originCursor + 1, cursor + 1)
                            .flatMap((entry) => (entry.lastMove === null ? [] : [entry.lastMove]))
                        : [],
                    labels: moveLabels,
                    initialAnnotations: lectureBoardProps.initialAnnotations,
                    annotations,
                    capturedAt: new Date().toISOString(),
                  });
                }}
              />
            ) : null}
          </div>
          {studio ? (
            <>
              {captureMessage ? (
                <p className="studio-record-caption" role="status">
                  {captureMessage}
                </p>
              ) : null}
              {state.error !== null && !foxSearchOpen ? (
                <p className="message message--error studio-error" role="alert">
                  {state.error}
                </p>
              ) : null}
              {aiEnabled && state.aiError !== null ? (
                <p className="message message--error studio-error" role="alert">
                  {state.aiError}
                </p>
              ) : null}
              {selectedRecord !== null ? (
                <p className="studio-record-caption">
                  {selectedRecord.black.nickname} 对 {selectedRecord.white.nickname}
                </p>
              ) : null}
              {localSgf !== null ? (
                <p className="studio-record-caption" role="status" title={localSgf.filename}>
                  已载入：{localSgf.filename} · 主线 {localSgf.frames.length - 1} 手
                </p>
              ) : null}
              {textbookQuestion !== null ? (
                <p className="studio-record-caption" role="status">
                  已载入教材题目：
                  {textbookQuestion.question.title || `第 ${textbookQuestion.question.number} 题`}
                  {' · '}
                  {textbookQuestion.question.currentPlayer === 'BLACK' ? '黑先' : '白先'}
                </p>
              ) : null}
              {state.pending === 'SGF_IMPORT' ? (
                <p className="studio-record-caption" role="status">
                  正在上传棋谱…
                </p>
              ) : null}
              <ClassroomStudioControls
                sourceRoutes={
                  branchWorkspace === null
                    ? undefined
                    : {
                        routes: branchWorkspace.branches.filter(
                          (branch) => branch.kind === 'SOURCE',
                        ),
                        currentId:
                          activeBranch?.kind === 'VARIATION'
                            ? activeBranch.originId
                            : branchWorkspace.activeId,
                        onSelect: switchClassroomBranch,
                      }
                }
                busy={positionPending}
                actionPending={state.pending !== null}
                placementMode={placementMode}
                alternateWhiteFirst={alternateStartColor === 'WHITE'}
                alternateHint={alternateHint}
                onAlternate={chooseAlternate}
                onPlacement={setPlacementMode}
                annotationOpen={annotationOpen}
                zoomed={boardCorner !== null}
                onZoom={() => {
                  setBoardCorner(null);
                  setBoardZoomMenuOpen(false);
                }}
                onZoomCorner={setBoardCorner}
                researching={researching}
                researchLabel={activeBranch?.kind === 'VARIATION' ? '结束' : undefined}
                researchAvailable={researchAvailable}
                markerMode={markerMode}
                customMarkerCount={customMarkerCount}
                onMarkerMode={(mode, count) => {
                  setMarkerMode(mode);
                  if (count !== undefined && Number.isSafeInteger(count) && count > 0)
                    setCustomMarkerCount(count);
                }}
                onResearch={toggleResearch}
                onRecords={() => {
                  setFoxSearchOpen(true);
                  foxSearchPanel.resetPosition();
                }}
                onUploadSgf={() => {
                  if (store.snapshot().pending !== null) return;
                  setAnnotationOpen(false);
                  setTextbookPanelOpen(false);
                  setSgfPanelState('OPEN');
                  if (sgfRecords.length === 0) chooseSgfFiles();
                }}
                onTextbooks={
                  ebookClient
                    ? () => {
                        if (store.snapshot().pending !== null) return;
                        setTextbookPickerOpen(true);
                      }
                    : undefined
                }
                onClear={() => {
                  if (store.snapshot().pending === null) resetBoard(boardSize);
                }}
                previousUnavailable={previousUnavailable}
                nextUnavailable={nextUnavailable}
                onFirst={() => navigateLecture('FIRST')}
                onPrevious={usePreviousMove}
                onNext={() => navigateLecture('NEXT')}
                onLast={() => navigateLecture('LAST')}
                moveNumber={researching ? researchCursor : cursor}
                totalMoves={researching ? variationMoves.length : totalMoves}
                progressDisabled={researching ? variationMoves.length === 0 : progressUnavailable}
                onProgress={navigateLecture}
                aiEnabled={aiEnabled}
                aiPending={state.aiPending}
                onAi={() => void toggleAi()}
              />
            </>
          ) : (
            <div className="fox-kifu-board-toolbar classroom-lecture-toolbar classroom-sidebar-controls">
              {state.error !== null && !foxSearchOpen ? (
                <p className="message message--error classroom-position-error" role="alert">
                  {state.error}
                </p>
              ) : null}
              <div className="classroom-control-heading">
                <span className="fox-kifu-move-status">
                  <strong>
                    {researching ? `变化第 ${researchCursor} 手` : `第 ${frame.moveNumber} 手`}
                  </strong>
                  <ClassroomPositionStatus
                    pending={positionPending}
                    caption={
                      practiceProblem?.title ??
                      selectedRecord?.resultText ??
                      `${studentPractice ? '自由摆棋' : '课堂讲解'} · ${boardSize}路`
                    }
                  />
                </span>
                <div className="classroom-board-actions" ref={boardSizeControlRef}>
                  <ClassroomControlButton
                    ref={boardSizeButtonRef}
                    className="classroom-switch-board"
                    type="button"
                    aria-expanded={boardSizeMenuOpen}
                    title={`当前为${boardSize}路棋盘`}
                    disabled={state.pending !== null && !positionPending}
                    busy={positionPending}
                    onClick={() => {
                      setPendingBoardSize(null);
                      setBoardSizeMenuOpen((open) => !open);
                    }}
                  >
                    切换棋盘
                  </ClassroomControlButton>
                  {boardSizeMenuOpen ? (
                    <ClassroomBoardSizeMenu
                      currentSize={boardSize}
                      disabled={state.pending !== null}
                      onSelect={chooseBoardSize}
                      pendingSize={pendingBoardSize}
                      onCancel={() => {
                        setPendingBoardSize(null);
                        setBoardSizeMenuOpen(false);
                        boardSizeButtonRef.current?.focus();
                      }}
                      onConfirm={() => {
                        if (pendingBoardSize === null || store.snapshot().pending !== null) return;
                        resetBoard(pendingBoardSize);
                        boardSizeButtonRef.current?.focus();
                      }}
                    />
                  ) : null}
                  <button
                    className="classroom-clear-board"
                    type="button"
                    onClick={() => resetBoard(boardSize)}
                  >
                    清空棋盘
                  </button>
                </div>
              </div>
              <div className="classroom-primary-controls">
                <div
                  className="fox-kifu-playback-buttons"
                  role="group"
                  aria-label="课堂棋盘前进与后退"
                >
                  <ClassroomControlButton
                    type="button"
                    disabled={previousUnavailable}
                    busy={positionPending}
                    onClick={() => navigateLecture('FIRST')}
                  >
                    {researching ? '变化起点' : '首手'}
                  </ClassroomControlButton>
                  <ClassroomControlButton
                    type="button"
                    disabled={previousUnavailable}
                    busy={positionPending}
                    onClick={usePreviousMove}
                  >
                    {researching ? '后退' : '上一手'}
                  </ClassroomControlButton>
                  <ClassroomControlButton
                    type="button"
                    disabled={nextUnavailable}
                    busy={positionPending}
                    onClick={() => navigateLecture('NEXT')}
                  >
                    {researching ? '前进' : '下一手'}
                  </ClassroomControlButton>
                  <ClassroomControlButton
                    type="button"
                    disabled={nextUnavailable}
                    busy={positionPending}
                    onClick={() => navigateLecture('LAST')}
                  >
                    {researching ? '变化末手' : '末手'}
                  </ClassroomControlButton>
                </div>
                <div className="classroom-placement-buttons" role="group" aria-label="课堂摆棋方式">
                  <ClassroomControlButton
                    className={placementMode === 'ALTERNATE' ? 'is-active' : ''}
                    type="button"
                    title="黑白交替；首次点击黑先，再次点击切换为白先"
                    aria-label={`黑白交替，${alternateStartColor === 'BLACK' ? '黑先' : '白先'}`}
                    disabled={researching || annotationOpen}
                    busy={positionPending}
                    onClick={chooseAlternate}
                  >
                    {alternateHint === null ? null : (
                      <span className="classroom-placement-hint" role="status">
                        {alternateHint}
                      </span>
                    )}
                    <span className="classroom-double-stone" aria-hidden="true">
                      <i
                        className={`stone-preview stone-preview--${alternateStartColor === 'BLACK' ? 'white' : 'black'}`}
                      />
                      <i
                        className={`stone-preview stone-preview--${alternateStartColor === 'BLACK' ? 'black' : 'white'}`}
                      />
                    </span>
                  </ClassroomControlButton>
                  <ClassroomControlButton
                    className={placementMode === 'BLACK_ONLY' ? 'is-active' : ''}
                    type="button"
                    title="左键添加黑子，右键添加白子，不记录手数；左键再次点击黑子可移除"
                    aria-label="只添加黑子"
                    disabled={researching || annotationOpen}
                    busy={positionPending}
                    onClick={() => setPlacementMode('BLACK_ONLY')}
                  >
                    <i className="stone-preview stone-preview--black" aria-hidden="true" />
                  </ClassroomControlButton>
                  <ClassroomControlButton
                    className={placementMode === 'WHITE_ONLY' ? 'is-active' : ''}
                    type="button"
                    title="左键添加白子，右键添加黑子，不记录手数；左键再次点击白子可移除"
                    aria-label="只添加白子"
                    disabled={researching || annotationOpen}
                    busy={positionPending}
                    onClick={() => setPlacementMode('WHITE_ONLY')}
                  >
                    <i className="stone-preview stone-preview--white" aria-hidden="true" />
                  </ClassroomControlButton>
                </div>
              </div>
              <div
                className="fox-kifu-tool-buttons classroom-analysis-tools"
                role="group"
                aria-label="课堂棋盘分析工具"
              >
                {studentPractice ? (
                  <StudentPracticeMarkerControl
                    markerMode={markerMode}
                    customMarkerCount={customMarkerCount}
                    onSelect={(mode, count) => {
                      setMarkerMode(mode);
                      if (count !== undefined) setCustomMarkerCount(count);
                    }}
                    onCustomChange={setCustomMarkerCount}
                  />
                ) : (
                  <button type="button" disabled={researching} onClick={cycleMarkerMode}>
                    标记：{classroomMarkerLabel(markerMode, customMarkerCount)}
                  </button>
                )}
                {studentPracticeClient ? (
                  <StudentPracticeQuestions
                    client={studentPracticeClient}
                    disabled={state.pending !== null}
                    onSelect={selectPracticeProblem}
                  />
                ) : (
                  <button
                    type="button"
                    className={`${aiEnabled ? 'is-active' : ''}${state.aiPending ? ' is-loading' : ''}`}
                    title={
                      aiEnabled
                        ? '暂停当前局面的持续分析'
                        : recordToolsAvailable
                          ? '显示当前棋谱局面的 AI 推荐点'
                          : '显示当前自由摆棋局面的 AI 推荐点'
                    }
                    onClick={() => void toggleAi()}
                  >
                    {state.aiPending ? '连接 / 分析中…' : aiEnabled ? '暂停AI' : 'AI算力'}
                  </button>
                )}
                <button
                  type="button"
                  className={annotationOpen ? 'is-active' : ''}
                  onClick={toggleAnnotationPanel}
                >
                  添加标记
                </button>
                <ClassroomControlButton
                  type="button"
                  className={researching ? 'is-active' : ''}
                  title={
                    researchAvailable
                      ? undefined
                      : studentPractice
                        ? '请先选择黑白交替模式'
                        : '请选择黑白交替模式，或载入一盘野狐棋谱'
                  }
                  disabled={!researchAvailable || annotationOpen}
                  busy={positionPending}
                  onClick={toggleResearch}
                >
                  {researching ? '结束试下' : '对局研究'}
                </ClassroomControlButton>
                <button
                  type="button"
                  className={boardCorner !== null ? 'is-active' : ''}
                  aria-label={boardCorner === null ? '选择棋盘放大区域' : '恢复全盘'}
                  onClick={() => {
                    if (boardCorner !== null) {
                      setBoardCorner(null);
                      setBoardZoomMenuOpen(false);
                      return;
                    }
                    setBoardZoomMenuOpen((open) => !open);
                  }}
                >
                  棋盘放大
                </button>
                {studentPractice ? (
                  <button type="button" disabled={capturing} onClick={() => void captureBoard()}>
                    {capturing ? '保存中…' : '截图'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={foxSearchOpen ? 'is-active' : ''}
                    aria-expanded={foxSearchOpen}
                    onClick={() => {
                      if (foxSearchOpen) foxSearchPanel.resetPosition();
                      setFoxSearchOpen((open) => !open);
                    }}
                  >
                    野狐棋谱
                  </button>
                )}
                {aiEnabled ? <span className="fox-kifu-ai-active-state">持续分析中</span> : null}
              </div>
              {studentPractice && captureMessage ? (
                <p className="student-practice-capture-message" role="status">
                  {captureMessage}
                </p>
              ) : null}
              {aiEnabled && state.aiError !== null ? (
                <button
                  className="fox-kifu-ai-error"
                  type="button"
                  onClick={() => store.clearAiError()}
                >
                  {state.aiError}
                </button>
              ) : null}
            </div>
          )}
        </aside>
      </ClassroomWorkspace>
      {studio && textbookPickerOpen && ebookClient ? (
        <ClassroomTextbookPicker
          client={ebookClient}
          onSelect={(book) => {
            setTextbook(book);
            setTextbookPickerOpen(false);
            setTextbookPanelOpen(true);
            setAnnotationOpen(false);
            setSgfPanelState((current) => (current === 'OPEN' ? 'MINIMIZED' : current));
          }}
          onClose={() => setTextbookPickerOpen(false)}
        />
      ) : null}
      {noteShare !== null ? (
        <ClassroomNoteShareDialog
          sections={noteShare}
          author={teacherName}
          onClose={() => setNoteShare(null)}
        />
      ) : null}
    </main>
  );
}
